import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '../utils/backendApi';
import { IS_UAT } from '../data/env';

// Releases tab of the fleet admin (/admin/apps → Releases). Talks to the
// operator-only /releases API: what runs on UAT and prod (health env+sha,
// SPA bundle), how far each is behind git head, Build → UAT, Promote → prod
// (type-the-word confirm, blocked while UAT is red / a deploy or provision
// job is in flight), and the release history with CodeBuild log links.

const short = (sha) => (sha ? String(sha).slice(0, 10) : '—');

function fmtWhen(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString();
}

function fmtAgo(ts) {
  if (!ts) return '—';
  const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

function HealthDot({ health }) {
  const cls = !health ? 'is-unknown' : health.ok ? 'is-up' : 'is-down';
  const label = !health ? 'probing' : health.ok ? 'healthy' : 'unreachable';
  return <span className={`aa-health-badge ${cls}`}>{label}</span>;
}

function EnvCard({ name, env, head, releaseFor, children }) {
  const sha = env?.health?.sha;
  const rel = sha ? releaseFor(sha) : null;
  const behind = env?.behindHead;
  return (
    <div className="cs-card aa-env-card">
      <div className="aa-env-head">
        <h3>{name}</h3>
        <HealthDot health={env?.health} />
      </div>
      <dl className="aa-env-facts">
        <dt>Backend</dt>
        <dd>
          <span className="aa-mono">{short(sha)}</span>
          {sha && head?.sha === sha ? <span className="aa-tag">= head</span> : null}
          {sha === null && env?.health?.ok ? <span className="cs-help"> break-glass build (no sha baked)</span> : null}
          {rel?.message ? <span className="aa-env-msg">{rel.message}</span> : null}
        </dd>
        <dt>SPA bundle</dt>
        <dd className="aa-mono">{env?.bundle || '—'}</dd>
        <dt>Behind head</dt>
        <dd>
          {behind == null ? '—' : behind.count === 0 ? 'up to date' : `${behind.count} commit${behind.count === 1 ? '' : 's'}`}
        </dd>
        <dt>Last deploy</dt>
        <dd>{rel ? fmtAgo(rel[name.toLowerCase()]?.finishedAt) : '—'}</dd>
        <dt>Endpoints</dt>
        <dd className="aa-env-links">
          <a href={env?.site} target="_blank" rel="noreferrer">{env?.site?.replace(/^https?:\/\//, '')}</a>
          <a href={`${env?.api}/health`} target="_blank" rel="noreferrer">{env?.api?.replace(/^https?:\/\//, '')}/health</a>
        </dd>
      </dl>
      {children}
    </div>
  );
}

function JobRow({ job, onCancel, busy }) {
  const st = job.meta?.status || 'queued';
  const running = st === 'queued' || st === 'running';
  return (
    <li className={`aa-stage is-${st === 'done' ? 'done' : st === 'error' ? 'error' : 'running'}`}>
      <span className="aa-stage-name">{job.kind === 'build' ? 'build → UAT' : 'promote → prod'}</span>
      <span className="aa-stage-status">{running ? (job.phase || st) : st}</span>
      <span className="aa-stage-detail">
        {fmtWhen(job.createdAt)}
        {job.requestedBy ? ` · ${job.requestedBy}` : ''}
        {job.error ? ` · ${job.error}` : ''}
        {job.logUrl ? (
          <>
            {' · '}
            <a href={job.logUrl} target="_blank" rel="noreferrer">CodeBuild log</a>
          </>
        ) : null}
        {running && job.buildId ? (
          <>
            {' · '}
            <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" disabled={busy} onClick={() => onCancel(job)}>
              cancel
            </button>
          </>
        ) : null}
      </span>
    </li>
  );
}

function ReleaseRow({ rel, envs, onPromote, onCancel, busy, blocked }) {
  const [open, setOpen] = useState(false);
  const onUat = envs?.uat?.health?.sha === rel.sha;
  const onProd = envs?.prod?.health?.sha === rel.sha;
  const canPromote = rel.uat?.status === 'done' && !onProd && !(rel.prod?.status === 'running' || rel.prod?.status === 'queued');
  return (
    <>
      <tr className="aa-row" onClick={() => setOpen((v) => !v)}>
        <td className="aa-mono">{short(rel.sha)}</td>
        <td>
          {rel.message || '—'}
          <div className="cs-help" style={{ margin: 0 }}>
            {rel.ref ? `${rel.ref} · ` : ''}{rel.author || ''}{rel.requestedBy ? ` · requested by ${rel.requestedBy}` : ''}
          </div>
        </td>
        <td><span className={`aa-rel-status is-${rel.status}`}>{rel.status}</span></td>
        <td>
          {onUat ? <span className="aa-tag aa-tag-env">UAT</span> : null}
          {onProd ? <span className="aa-tag aa-tag-env is-prod">PROD</span> : null}
          {rel.evals ? <span className="aa-tag" title={`${rel.evals.reason}${rel.evals.run ? ` · ${rel.evals.run.passed}/${rel.evals.run.total}` : ''}`} style={{ color: rel.evals.ok ? 'var(--ok, #16a34a)' : 'var(--danger, #dc2626)' }}>{rel.evals.ok ? 'evals ✓' : 'evals ✗'}</span> : null}
          {rel.platformHealth ? <span className="aa-tag" title={`UAT feature health · ${rel.platformHealth.passed}/${rel.platformHealth.total}${rel.platformHealth.failing?.length ? ` · failing: ${rel.platformHealth.failing.map((f) => f.id).join(', ')}` : ''}`} style={{ color: rel.platformHealth.ok ? 'var(--ok, #16a34a)' : 'var(--danger, #dc2626)' }}>{rel.platformHealth.ok ? 'features ✓' : `features ✗ ${rel.platformHealth.failing?.length || ''}`}</span> : null}
        </td>
        <td>{fmtAgo(rel.updatedAt || rel.createdAt)}</td>
        <td onClick={(e) => e.stopPropagation()}>
          {canPromote ? (
            <button
              type="button"
              className="cs-btn cs-btn-sm cs-btn-outline"
              disabled={busy || !!blocked}
              title={blocked || 'Promote this UAT-verified build to prod'}
              onClick={() => onPromote(rel)}
            >
              Promote → prod
            </button>
          ) : null}
        </td>
      </tr>
      {open && (
        <tr className="aa-detail-row">
          <td colSpan={6}>
            <div className="aa-job-head">
              image {rel.imageTag || '—'}{rel.mainJs ? ` · bundle ${rel.mainJs}` : ''}
              {rel.prodMainJs && rel.mainJs && rel.prodMainJs !== rel.mainJs ? ' · ⚠ prod bundle differs' : ''}
            </div>
            <ol className="aa-stages">
              {(rel.jobs || []).map((j) => <JobRow key={j.id} job={j} onCancel={onCancel} busy={busy} />)}
              {!rel.jobs?.length && <li className="cs-help">No jobs recorded.</li>}
            </ol>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminReleases({ notify }) {
  const [envs, setEnvs] = useState(null);
  const [releases, setReleases] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState('');
  const [promoting, setPromoting] = useState(null); // release awaiting confirm
  const [confirmWord, setConfirmWord] = useState('');
  const [force, setForce] = useState(false);

  const load = useCallback(async () => {
    try {
      // Sequential on purpose: the environments probe backfills releases/{sha}
      // for CLI-shipped builds, and the list should include them.
      const e = await apiGet('/releases/environments');
      setEnvs(e);
      const r = await apiGet('/releases?limit=40');
      setReleases(r.releases || []);
      setError(null);
    } catch (err) {
      setError(err.status === 403 ? 'Operator access required.' : err.message);
    }
  }, []);

  const active = useMemo(() => (envs?.gates?.activeDeployJobs || []).length > 0, [envs]);

  useEffect(() => {
    load();
    // Poll faster while something is deploying so phases tick live.
    const t = setInterval(load, active ? 8_000 : 30_000);
    return () => clearInterval(t);
  }, [load, active]);

  const releaseFor = useCallback((sha) => (releases || []).find((r) => r.sha === sha) || null, [releases]);

  const build = async () => {
    setBusy(true);
    try {
      const out = await apiPost('/releases/build', { ref: ref.trim() || envs?.branch || 'main' });
      notify?.(out.reused ? `build already running for ${short(out.releaseId)}` : `build → UAT queued for ${short(out.releaseId)} (${out.commit?.message || ''})`);
      setRef('');
      await load();
    } catch (err) {
      notify?.(`build failed to queue: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const promote = async () => {
    if (!promoting) return;
    setBusy(true);
    try {
      await apiPost(`/releases/${promoting.id}/promote`, { force });
      notify?.(`promote → prod queued for ${short(promoting.sha)}`);
      setPromoting(null);
      setConfirmWord('');
      setForce(false);
      await load();
    } catch (err) {
      notify?.(`promote refused: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (job) => {
    setBusy(true);
    try {
      await apiPost(`/releases/jobs/${job.id}/cancel`, {});
      notify?.(`cancel requested for ${job.kind} ${short(job.sha)} — CodeBuild stops within a poll`);
    } catch (err) {
      notify?.(`cancel failed: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const gates = envs?.gates || {};
  const uatRed = envs && !envs.uat?.health?.ok;
  // Hard blocks never yield; the provision-job block can be forced.
  const hardBlock = IS_UAT
    ? 'releases are driven from the prod control plane (nventr.io)'
    : uatRed
      ? 'UAT is unhealthy — fix it before promoting'
      : active
        ? 'a deploy job is already running'
        : '';
  const provBlock = gates.activeProvisionJobs ? `${gates.activeProvisionJobs} provision job(s) in flight` : '';
  const evalsBlock = promoting?.evals && promoting.evals.ok === false ? `golden-set evals failed on UAT (${promoting.evals.reason})` : '';
  const featuresBlock = promoting?.platformHealth && promoting.platformHealth.ok === false ? `${promoting.platformHealth.failing?.length || ''} feature journeys failed on UAT` : '';
  const blocked = hardBlock || provBlock;

  if (error) return <div className="cs-card"><p className="cs-help">{error}</p></div>;

  return (
    <>
      <h2>
        Environments{' '}
        <span className="cs-help">
          {envs ? `${envs.repo} · ${envs.branch} head ${short(envs.head?.sha)}${envs.head?.message ? ` — ${envs.head.message}` : ''}` : '…'}
        </span>
      </h2>
      <div className="aa-env-grid">
        <EnvCard name="UAT" env={envs?.uat} head={envs?.head} releaseFor={releaseFor}>
          <div className="aa-env-actions">
            <input
              className="cs-input"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder={envs?.branch || 'main'}
              disabled={busy || IS_UAT}
              aria-label="git ref to build"
            />
            <button type="button" className="cs-btn cs-btn-sm" onClick={build} disabled={busy || active || IS_UAT} title={IS_UAT ? blocked : ''}>
              Build → UAT
            </button>
          </div>
          <p className="cs-help">
            Builds the image once (<span className="aa-mono">git-&lt;sha&gt;</span>), rolls the UAT service, deploys UAT hosting + rules, archives the SPA build.
          </p>
        </EnvCard>
        <EnvCard name="Prod" env={envs?.prod} head={envs?.head} releaseFor={releaseFor}>
          <p className="cs-help">
            {envs?.bundlesMatch ? 'Prod serves the same SPA bundle as UAT.' : envs ? 'Prod and UAT serve different SPA bundles.' : ''}
            {blocked && !IS_UAT ? ` Promote is blocked: ${blocked}.` : ' Promote from a UAT-verified release below.'}
          </p>
          {envs?.prod?.behindHead?.commits?.length ? (
            <details className="aa-behind">
              <summary>{envs.prod.behindHead.count} commit{envs.prod.behindHead.count === 1 ? '' : 's'} not yet in prod</summary>
              <ul>
                {envs.prod.behindHead.commits.map((c) => (
                  <li key={c.sha}><span className="aa-mono">{short(c.sha)}</span> {c.message} <span className="cs-help">{c.author}</span></li>
                ))}
              </ul>
            </details>
          ) : null}
        </EnvCard>
      </div>

      {promoting && (
        <div className="cs-card aa-confirm" style={{ marginTop: 12 }} data-agent-guard>
          <span>
            Promote <strong className="aa-mono">{short(promoting.sha)}</strong> ({promoting.message}) to <strong>prod</strong>: rolls the prod backend to
            the UAT-verified image, clones UAT's live hosting release to nventr.io, deploys rules. Type <strong>promote</strong> to confirm:
          </span>
          <input className="cs-input" value={confirmWord} onChange={(e) => setConfirmWord(e.target.value)} placeholder="promote" />
          {provBlock || evalsBlock || featuresBlock ? (
            <label className="aa-launch-check">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
              force despite {[provBlock && `${gates.activeProvisionJobs} provision job(s) in flight (they may strand mid-stage)`, evalsBlock, featuresBlock].filter(Boolean).join(' and ')}
            </label>
          ) : null}
          <button type="button" className="cs-btn cs-btn-sm" disabled={busy || confirmWord !== 'promote' || !!hardBlock || ((!!provBlock || !!evalsBlock || !!featuresBlock) && !force)} onClick={promote}>
            Promote
          </button>
          <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => { setPromoting(null); setConfirmWord(''); setForce(false); }}>
            Cancel
          </button>
        </div>
      )}

      <h2 style={{ marginTop: 28 }}>
        Releases <span className="cs-help">{releases ? `${releases.length} recorded` : '…'}</span>
      </h2>
      <div className="cs-card aa-tablewrap">
        <table className="aa-table">
          <thead>
            <tr><th>Sha</th><th>Commit</th><th>Status</th><th>Live on</th><th>Updated</th><th /></tr>
          </thead>
          <tbody>
            {(releases || []).map((rel) => (
              <ReleaseRow key={rel.id} rel={rel} envs={envs} onPromote={setPromoting} onCancel={cancel} busy={busy} blocked={blocked} />
            ))}
            {releases && !releases.length && (
              <tr><td colSpan={6} className="cs-help">No releases yet — Build → UAT starts the first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="cs-help">
        CLI parity: <code>scripts/release.sh build &lt;ref&gt;</code> · <code>scripts/release.sh promote &lt;sha&gt;</code>. The pipeline is{' '}
        <code>backend/buildspec-release.yml</code>; <code>deploy-*.sh</code> remain break-glass only.
      </p>
    </>
  );
}
